import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

const GymOwnerSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    gymName: '',
    gymAddress: '',
    numberOfLocations: '1',
    zelleEmail: ''
  });

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData?.firstName?.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData?.lastName?.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData?.email?.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData?.phone?.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (!formData?.password) {
      setError('Password is required');
      return false;
    }
    if (formData?.password?.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData?.gymName?.trim()) {
      setError('Gym name is required');
      return false;
    }
    if (!formData?.gymAddress?.trim()) {
      setError('Gym address is required');
      return false;
    }
    if (!formData?.numberOfLocations) {
      setError('Please select number of gym locations');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    // Navigate to subscription and payment page with form data
    navigate('/gym-owner-signup/subscription-payment', {
      state: { formData }
    });
  };

  const locationOptions = [
    { value: '1', label: 'Single Location' },
    { value: '2-5', label: '2-5 Locations' },
    { value: '6-10', label: '6-10 Locations' },
    { value: '11+', label: '11+ Locations' }
  ];

  return (
    <>
      <Helmet>
        <title>Gym Owner Signup - TrainaryGym</title>
        <meta name="description" content="Sign up as a gym owner to manage your fitness facility with TrainaryGym's comprehensive management platform." />
      </Helmet>
      <div className="min-h-screen flex items-center justify-center p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/login" className="inline-flex items-center gap-2 mb-6 text-muted-foreground hover:text-foreground transition-colors">
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm">Back to Login</span>
            </Link>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Building2" size={24} color="var(--color-primary)" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-foreground">TrainaryGym</h1>
            </div>
            <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
              Gym Owner Registration
            </h2>
            <p className="text-muted-foreground">
              Join TrainaryGym and start managing your fitness facility with ease
            </p>
          </div>

          {/* Registration Form */}
          <div className="card">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                  <p className="text-sm text-destructive flex-1">{error}</p>
                </div>
              )}

              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="User" size={20} color="var(--color-primary)" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    name="firstName"
                    value={formData?.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    required
                  />
                  <Input
                    label="Last Name"
                    name="lastName"
                    value={formData?.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    required
                  />
                </div>
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="john.doe@example.com"
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  name="phone"
                  value={formData?.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>

              {/* Gym Information */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Building2" size={20} color="var(--color-primary)" />
                  Gym Information
                </h3>
                <Input
                  label="Gym Name"
                  name="gymName"
                  value={formData?.gymName}
                  onChange={handleChange}
                  placeholder="Your Gym Name"
                  required
                />
                <Input
                  label="Gym Address"
                  name="gymAddress"
                  value={formData?.gymAddress}
                  onChange={handleChange}
                  placeholder="123 Main St, City, State, ZIP"
                  required
                />
                <Select
                  label="Number of Gym Locations"
                  name="numberOfLocations"
                  value={formData?.numberOfLocations}
                  onChange={handleChange}
                  options={locationOptions}
                  required
                />
              </div>

              {/* Payment Configuration */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="DollarSign" size={20} color="var(--color-primary)" />
                  Payment Configuration
                </h3>
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-4">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">Zelle Payment Setup</p>
                      <p className="text-xs text-muted-foreground">
                        Configure your Zelle email to receive payments from members. This is optional but recommended for flexible payment options.
                      </p>
                    </div>
                  </div>
                  <Input
                    label="Zelle Email Address (Optional)"
                    type="email"
                    name="zelleEmail"
                    value={formData?.zelleEmail}
                    onChange={handleChange}
                    placeholder="your-gym@example.com"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Members will be able to pay their membership fees via Zelle to this email address. Payments require manual approval.
                  </p>
                </div>
              </div>

              {/* Security */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Lock" size={20} color="var(--color-primary)" />
                  Security
                </h3>
                <Input
                  label="Password"
                  type="password"
                  name="password"
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="Minimum 8 characters"
                  required
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  name="confirmPassword"
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  required
                />
                {formData?.password && formData?.password?.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs">
                      <Icon
                        name={formData?.password?.length >= 8 ? 'CheckCircle2' : 'Circle'}
                        size={14}
                        color={formData?.password?.length >= 8 ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                      />
                      <span className={formData?.password?.length >= 8 ? 'text-success' : 'text-muted-foreground'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Icon
                        name={formData?.password === formData?.confirmPassword && formData?.confirmPassword ? 'CheckCircle2' : 'Circle'}
                        size={14}
                        color={formData?.password === formData?.confirmPassword && formData?.confirmPassword ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                      />
                      <span className={formData?.password === formData?.confirmPassword && formData?.confirmPassword ? 'text-success' : 'text-muted-foreground'}>
                        Passwords match
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                iconName="ArrowRight"
                iconPosition="right"
              >
                Continue to Subscription & Payment
              </Button>

              {/* Login Link */}
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default GymOwnerSignup;